import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, LogIn, Loader2 } from "lucide-react";

export default function AdminLoginPage() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (admin && typeof admin === "object") {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue", { description: "Connexion réussie." });
      navigate("/admin");
    } catch (err) {
      toast.error("Connexion refusée", {
        description: err?.response?.data?.detail || "Identifiants invalides",
      });
    } finally { setLoading(false); }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen navy-pattern flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-white p-10 border border-navy-100 relative"
      >
        <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-navy-500" />
        <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-navy-500" />

        <div className="flex items-center gap-3 mb-2">
          <Lock className="h-5 w-5 text-navy-500" />
          <div className="overline">Espace Administration</div>
        </div>
        <h1 className="font-display text-3xl text-navy-700 mb-8 leading-tight">
          Connexion <em className="not-italic red-shine">admin</em>
        </h1>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
          <div>
            <Label htmlFor="ad-email" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Email</Label>
            <Input
              id="ad-email" type="email" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
              data-testid="admin-input-email"
            />
          </div>
          <div>
            <Label htmlFor="ad-pwd" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Mot de passe</Label>
            <Input
              id="ad-pwd" type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
              data-testid="admin-input-password"
            />
          </div>
          <button type="submit" disabled={loading} className="btn-red w-full justify-center disabled:opacity-60" data-testid="admin-login-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
